// src/features/sheets/store/sheetsStore.ts
// Zustand store for managing spreadsheets

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Sheet, SheetsStore, CellFormat, SheetAIMetadata } from "../types";
import { DEFAULT_ROWS, DEFAULT_COLS, DEFAULT_COL_WIDTH, getCellKey } from "../types";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const generateId = () => `sheet_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const createEmptyGrid = (rows: number, cols: number): string[][] =>
  Array.from({ length: rows }, () => Array.from({ length: cols }, () => ""));

const createDefaultSheet = (name?: string): Sheet => {
  const now = new Date().toISOString();
  return {
    id: generateId(),
    name: name || `Sheet ${Date.now()}`,
    data: createEmptyGrid(DEFAULT_ROWS, DEFAULT_COLS),
    formats: {},
    colWidths: Array(DEFAULT_COLS).fill(DEFAULT_COL_WIDTH),
    rows: DEFAULT_ROWS,
    cols: DEFAULT_COLS,
    createdAt: now,
    updatedAt: now,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useSheetsStore = create<SheetsStore>()(
  persist(
    (set, get) => ({
      // Initial state
      sheets: [],
      activeSheetId: null,
      isLoading: false,
      isSaving: false,

      // ─────────────────────────────────────────────────────────────────────────
      // Sheet CRUD
      // ─────────────────────────────────────────────────────────────────────────

      createSheet: (name?: string) => {
        const newSheet = createDefaultSheet(name);
        set((state) => ({
          sheets: [...state.sheets, newSheet],
          activeSheetId: newSheet.id,
        }));
        return newSheet;
      },

      importSheet: (
        name: string,
        data: string[][],
        formats?: Record<string, CellFormat>,
        colWidths?: number[]
      ) => {
        const now = new Date().toISOString();
        const rows = data.length || DEFAULT_ROWS;
        const cols = data[0]?.length || DEFAULT_COLS;
        const newSheet: Sheet = {
          id: generateId(),
          name,
          data: data.map((row) => [...row]), // Deep copy
          formats: formats ? { ...formats } : {},
          colWidths: colWidths ? [...colWidths] : Array(cols).fill(DEFAULT_COL_WIDTH),
          rows,
          cols,
          createdAt: now,
          updatedAt: now,
        };
        set((state) => ({
          sheets: [...state.sheets, newSheet],
          activeSheetId: newSheet.id,
        }));
        return newSheet;
      },

      deleteSheet: (id: string) => {
        set((state) => {
          const newSheets = state.sheets.filter((s) => s.id !== id);
          const newActiveId =
            state.activeSheetId === id
              ? newSheets[0]?.id ?? null
              : state.activeSheetId;
          return {
            sheets: newSheets,
            activeSheetId: newActiveId,
          };
        });
      },

      duplicateSheet: (id: string) => {
        const sheet = get().sheets.find((s) => s.id === id);
        if (!sheet) return null;

        const now = new Date().toISOString();
        const newSheet: Sheet = {
          ...sheet,
          id: generateId(),
          name: `${sheet.name} (Copy)`,
          data: sheet.data.map((row) => [...row]),
          formats: { ...sheet.formats },
          colWidths: [...sheet.colWidths],
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          sheets: [...state.sheets, newSheet],
          activeSheetId: newSheet.id,
        }));
        return newSheet;
      },

      renameSheet: (id: string, name: string) => {
        set((state) => ({
          sheets: state.sheets.map((s) =>
            s.id === id
              ? { ...s, name, updatedAt: new Date().toISOString() }
              : s
          ),
        }));
      },

      setActiveSheet: (id: string | null) => {
        set({ activeSheetId: id });
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Cell Operations
      // ─────────────────────────────────────────────────────────────────────────

      updateCell: (sheetId: string, row: number, col: number, value: string) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            if (s.id !== sheetId) return s;
            // Bounds checking: ignore out-of-bounds updates
            if (row < 0 || row >= s.rows || col < 0 || col >= s.cols) {
              console.warn(`updateCell: out of bounds (row=${row}, col=${col}, sheet=${s.rows}x${s.cols})`);
              return s;
            }
            const newData = s.data.map((r, ri) =>
              ri === row
                ? r.map((c, ci) => (ci === col ? value : c))
                : r
            );
            return {
              ...s,
              data: newData,
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      updateCells: (
        sheetId: string,
        updates: Array<{ row: number; col: number; value: string }>
      ) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            if (s.id !== sheetId) return s;
            const newData = s.data.map((row) => [...row]);
            updates.forEach(({ row, col, value }) => {
              // Bounds checking: skip out-of-bounds updates
              if (row >= 0 && row < s.rows && col >= 0 && col < s.cols && newData[row]) {
                newData[row][col] = value;
              }
            });
            return {
              ...s,
              data: newData,
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      setCellFormat: (
        sheetId: string,
        row: number,
        col: number,
        format: Partial<CellFormat>
      ) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            if (s.id !== sheetId) return s;
            // Bounds checking: ignore out-of-bounds format updates
            if (row < 0 || row >= s.rows || col < 0 || col >= s.cols) {
              return s;
            }
            const key = getCellKey(row, col);
            return {
              ...s,
              formats: {
                ...s.formats,
                [key]: { ...s.formats[key], ...format },
              },
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      // ─────────────────────────────────────────────────────────────────────────
      // Structure Operations
      // ─────────────────────────────────────────────────────────────────────────

      addRow: (sheetId: string, afterRow?: number) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            if (s.id !== sheetId) return s;
            // Validate afterRow bounds
            const insertAt = afterRow !== undefined
              ? Math.max(0, Math.min(afterRow + 1, s.rows))
              : s.rows;
            const newRow = Array(s.cols).fill("");
            const newData = [
              ...s.data.slice(0, insertAt),
              newRow,
              ...s.data.slice(insertAt),
            ];
            // Shift format keys for rows at/below the insertion point
            const newFormats: Record<string, CellFormat> = {};
            Object.entries(s.formats).forEach(([key, format]) => {
              const parts = key.split(",").map(Number);
              const r = parts[0];
              const c = parts[1];
              if (r === undefined || c === undefined) return;
              // Shift rows at or below insertion point down by 1
              const newRowIdx = r >= insertAt ? r + 1 : r;
              newFormats[getCellKey(newRowIdx, c)] = format;
            });
            return {
              ...s,
              data: newData,
              formats: newFormats,
              rows: s.rows + 1,
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      deleteRow: (sheetId: string, row: number) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            // Bounds checking: row must be valid and keep at least 1 row
            if (s.id !== sheetId || s.rows <= 1 || row < 0 || row >= s.rows) return s;
            const newData = s.data.filter((_, i) => i !== row);
            // Clean up formats for deleted row and shift format keys for rows below
            const newFormats: Record<string, CellFormat> = {};
            Object.entries(s.formats).forEach(([key, format]) => {
              const parts = key.split(",").map(Number);
              const r = parts[0];
              const c = parts[1];
              if (r === undefined || c === undefined) return;
              if (r === row) {
                // Skip deleted row's formats
                return;
              }
              // Shift rows above the deleted row
              const newRow = r > row ? r - 1 : r;
              newFormats[getCellKey(newRow, c)] = format;
            });
            return {
              ...s,
              data: newData,
              formats: newFormats,
              rows: s.rows - 1,
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      addColumn: (sheetId: string, afterCol?: number) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            if (s.id !== sheetId) return s;
            // Validate afterCol bounds
            const insertAt = afterCol !== undefined
              ? Math.max(0, Math.min(afterCol + 1, s.cols))
              : s.cols;
            const newData = s.data.map((row) => [
              ...row.slice(0, insertAt),
              "",
              ...row.slice(insertAt),
            ]);
            const newColWidths = [
              ...s.colWidths.slice(0, insertAt),
              DEFAULT_COL_WIDTH,
              ...s.colWidths.slice(insertAt),
            ];
            // Shift format keys for columns at/to the right of the insertion point
            const newFormats: Record<string, CellFormat> = {};
            Object.entries(s.formats).forEach(([key, format]) => {
              const parts = key.split(",").map(Number);
              const r = parts[0];
              const c = parts[1];
              if (r === undefined || c === undefined) return;
              // Shift columns at or to the right of insertion point right by 1
              const newColIdx = c >= insertAt ? c + 1 : c;
              newFormats[getCellKey(r, newColIdx)] = format;
            });
            return {
              ...s,
              data: newData,
              colWidths: newColWidths,
              formats: newFormats,
              cols: s.cols + 1,
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      deleteColumn: (sheetId: string, col: number) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            // Bounds checking: col must be valid and keep at least 1 column
            if (s.id !== sheetId || s.cols <= 1 || col < 0 || col >= s.cols) return s;
            const newData = s.data.map((row) =>
              row.filter((_, i) => i !== col)
            );
            const newColWidths = s.colWidths.filter((_, i) => i !== col);
            // Clean up formats for deleted column and shift format keys for columns to the right
            const newFormats: Record<string, CellFormat> = {};
            Object.entries(s.formats).forEach(([key, format]) => {
              const parts = key.split(",").map(Number);
              const r = parts[0];
              const c = parts[1];
              if (r === undefined || c === undefined) return;
              if (c === col) {
                // Skip deleted column's formats
                return;
              }
              // Shift columns to the right of the deleted column
              const newCol = c > col ? c - 1 : c;
              newFormats[getCellKey(r, newCol)] = format;
            });
            return {
              ...s,
              data: newData,
              colWidths: newColWidths,
              formats: newFormats,
              cols: s.cols - 1,
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      setColumnWidth: (sheetId: string, col: number, width: number) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            // Bounds checking
            if (s.id !== sheetId || col < 0 || col >= s.cols) return s;
            const newColWidths = [...s.colWidths];
            newColWidths[col] = Math.max(20, width); // Minimum width of 20px
            return {
              ...s,
              colWidths: newColWidths,
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },

      // ─────────────────────────────────────────────────────────────────────────
      // AI Metadata Operations
      // ─────────────────────────────────────────────────────────────────────────

      updateSheetMetadata: (sheetId: string, metadata: Partial<SheetAIMetadata>) => {
        set((state) => ({
          sheets: state.sheets.map((s) => {
            if (s.id !== sheetId) return s;
            return {
              ...s,
              aiMetadata: s.aiMetadata
                ? { ...s.aiMetadata, ...metadata }
                : {
                    purpose: "custom" as const,
                    analyzedAssets: [],
                    columnMetadata: [],
                    ...metadata,
                  },
              updatedAt: new Date().toISOString(),
            };
          }),
        }));
      },
    }),
    {
      name: "abi-sheets-storage",
      version: 1,
    }
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────────────────────────────────────

export const useActiveSheet = () => {
  const sheets = useSheetsStore((s) => s.sheets);
  const activeSheetId = useSheetsStore((s) => s.activeSheetId);
  return sheets.find((s) => s.id === activeSheetId) ?? null;
};

export const useSheetById = (id: string | null) => {
  const sheets = useSheetsStore((s) => s.sheets);
  return id ? sheets.find((s) => s.id === id) ?? null : null;
};
